import userService from '../../services/user.service';
import {
  APPLICATION_REFRESH_DATE,
  APP_INIT_DEFERRED,
  AUTH_LOGIN,
  INITIAL_STATE,
  KNOWLEDGE_CATALOG_DROP,
  KNOWLEDGE_CREATE_ITEM,
  KNOWLEDGE_CREATE_LINK,
  KNOWLEDGE_DASHBOARD_UPDATE_CONTENT,
  KNOWLEDGE_DELETE_CATEGORY,
  KNOWLEDGE_DELETE_ITEM,
  KNOWLEDGE_DELETE_LINK,
  KNOWLEDGE_MOVE_LINK,
  KNOWLEDGE_UPDATE_CATEGORY,
  KNOWLEDGE_UPDATE_CATEGORY_LOCATION,
  KNOWLEDGE_UPDATE_CONTENT,
  KNOWLEDGE_UPDATE_ITEM,
  KNOWLEDGE_UPDATE_ITEM_LOCATION,
  KNOWLEDGE_TRIGGER_UPDATE,
  KNOWLEDGE_UPDATE_CATEGORIES,
  KNOWLEDGE_USE_CACHE,
  NETWORK_CONNECTION_CHANGE_ONLINE,
  ORGANIZATION_SWITCH,
  ORGANIZATION_UPDATE,
  OWNERS_TRANSFER_OWNERSHIP,
} from '../actions';
import {
  getKnowledgeCategories,
  getKnowledgeContent,
  getKnowledgeDashboardContent,
} from '../actions/knowledge.actions';
import { updateStateToStorage } from '../actions/storage.actions';
import { needsKnowledgeCatalog } from '../selectors/features.selector';
import { invalidateQueryCache } from '../actions/query-cache.actions';
import { queryKeys } from '../../api/query-client-registry';

/**
 * Web owns the dashboard widgets through knowledge-dashboard.hooks.js; this
 * boot-time fan-out rides a `requestIdleCallback` and landed inside the window
 * RUM measures. Mobile still reads them from Redux, so the action stays.
 */
const shouldFetchDashboardIntoRedux = () => global.platform !== 'web';

// After boot only the tree is refreshed; the item rows are updated by the
// mutation reducers. A client that still needs the catalog downloads it in
// full when no rows ever loaded (offline boot with an empty cache), and
// widget-app does so on every refresh until it is deleted.
const refreshCatalog = (dispatch, getState, options) => {
  const state = getState();
  const rowsMissing = needsKnowledgeCatalog(state) && state.knowledge?.items == null;
  dispatch(
    global.isWidget || rowsMissing ? getKnowledgeContent(options) : getKnowledgeCategories(options),
  );
};

const refreshDashboard = dispatch => {
  if (shouldFetchDashboardIntoRedux()) dispatch(getKnowledgeDashboardContent());
  dispatch(invalidateQueryCache(queryKeys.knowledgeDashboard));
};

export const knowledgeDataFetchedMiddleware =
  ({ dispatch }) =>
  next =>
  action => {
    next(action);

    if (
      action.type === KNOWLEDGE_UPDATE_CONTENT ||
      action.type === KNOWLEDGE_UPDATE_CATEGORIES ||
      action.type === KNOWLEDGE_CATALOG_DROP ||
      action.type === KNOWLEDGE_DASHBOARD_UPDATE_CONTENT
    ) {
      dispatch(updateStateToStorage());
    }
  };

export const knowledgeCacheMiddleware =
  ({ dispatch, getState }) =>
  next =>
  action => {
    next(action);

    if (action.type === INITIAL_STATE) {
      const { knowledge } = getState();
      const hasCachedData = knowledge.categories?.length > 0 || knowledge.items?.length > 0;

      if (hasCachedData) {
        dispatch({ type: KNOWLEDGE_USE_CACHE });
      }
    }
  };

/**
 * Keeps the store's item rows in step with the organization's mode. Runs when
 * the persisted state lands (a blob an older build wrote still holds every row)
 * and whenever the organization loads (a switch, or the search-v2 flag flipped).
 * Rows a client no longer needs are dropped, along with a download in flight;
 * a client that needs them and has none starts the download. The rows of
 * downloaded files are kept, so a saved item still opens offline.
 *
 * @param {{ dispatch: Function, getState: () => object }} store - The store API.
 * @returns {(next: Function) => (action: { type: string }) => void} The middleware.
 * @example
 * applyMiddleware(knowledgeCatalogModeMiddleware);
 */
export const knowledgeCatalogModeMiddleware =
  ({ dispatch, getState }) =>
  next =>
  action => {
    next(action);

    if (action.type !== INITIAL_STATE && action.type !== ORGANIZATION_UPDATE) return;

    const state = getState();
    const { items, catalogRequestId } = state.knowledge ?? {};

    if (!needsKnowledgeCatalog(state)) {
      if (items != null || catalogRequestId) {
        const keepIds = Object.keys(state.fs?.map ?? {});
        dispatch({ type: KNOWLEDGE_CATALOG_DROP, payload: { keepIds } });
      }
      // The dropped download was also fetching the category tree.
      if (catalogRequestId && userService.isAuthenticated) dispatch(getKnowledgeCategories());
      return;
    }

    if (action.type === ORGANIZATION_UPDATE && items == null && userService.isAuthenticated) {
      dispatch(getKnowledgeContent());
    }
  };

export const knowledgeFetchDataMiddleware =
  ({ dispatch, getState }) =>
  next =>
  async action => {
    next(action);

    // ORGANIZATION_SWITCH reaches here after organization.middleware wiped the
    // store and swapped the token, so the download runs against the new org.
    if (
      action.type === AUTH_LOGIN ||
      action.type === APP_INIT_DEFERRED ||
      action.type === ORGANIZATION_SWITCH ||
      action.type === APPLICATION_REFRESH_DATE ||
      action.type === OWNERS_TRANSFER_OWNERSHIP
    ) {
      if (!userService.isAuthenticated) return false;

      dispatch(getKnowledgeContent());
      if (shouldFetchDashboardIntoRedux()) dispatch(getKnowledgeDashboardContent());

      // Excludes the boot/auth actions: the dashboard mounts after them and
      // fetches on its own, so invalidating would add a second round of widget
      // requests inside the measured load window.
      if (action.type === APPLICATION_REFRESH_DATE || action.type === OWNERS_TRANSFER_OWNERSHIP) {
        dispatch(invalidateQueryCache(queryKeys.knowledgeDashboard));
        dispatch(invalidateQueryCache(queryKeys.knowledgeItems));
      }
    }
  };

export const knowledgeDataReconnectToNetworkMiddleware =
  ({ dispatch, getState }) =>
  next =>
  action => {
    next(action);

    if (action.type === NETWORK_CONNECTION_CHANGE_ONLINE) {
      refreshCatalog(dispatch, getState);
      refreshDashboard(dispatch);
    }
  };

export const knowledgeOnCategoryChangeOwnerIdMiddleware =
  ({ dispatch, getState }) =>
  next =>
  async action => {
    next(action);

    if (action.type === KNOWLEDGE_UPDATE_CATEGORY) {
      if (!action.payload.ownerIds) return false;

      refreshCatalog(dispatch, getState, { refetchIfRunning: true });
      // The owner cascade changes `ownerIds` on the binder's item rows too.
      dispatch(invalidateQueryCache(queryKeys.knowledgeItems));
    }
  };

export const knowledgeOnItemUpdateFetchMetadata =
  ({ dispatch, getState }) =>
  next =>
  async action => {
    next(action);

    if (
      action.type === KNOWLEDGE_CREATE_ITEM ||
      action.type === KNOWLEDGE_DELETE_ITEM ||
      action.type === KNOWLEDGE_UPDATE_ITEM ||
      action.type === KNOWLEDGE_UPDATE_ITEM_LOCATION ||
      action.type === KNOWLEDGE_CREATE_LINK ||
      action.type === KNOWLEDGE_DELETE_LINK ||
      action.type === KNOWLEDGE_MOVE_LINK ||
      action.type === KNOWLEDGE_DELETE_CATEGORY ||
      action.type === KNOWLEDGE_UPDATE_CATEGORY_LOCATION ||
      action.type === KNOWLEDGE_TRIGGER_UPDATE
    ) {
      // A change may postdate pages a running download already fetched.
      refreshCatalog(dispatch, getState, { refetchIfRunning: true });
      refreshDashboard(dispatch);
      dispatch(invalidateQueryCache(queryKeys.knowledgeItems));
    }
  };

export const knowledgeMiddleware = [
  knowledgeCacheMiddleware,
  knowledgeCatalogModeMiddleware,
  knowledgeDataFetchedMiddleware,
  knowledgeDataReconnectToNetworkMiddleware,
  knowledgeFetchDataMiddleware,
  knowledgeOnCategoryChangeOwnerIdMiddleware,
  knowledgeOnItemUpdateFetchMetadata,
];
